// apps/client/src/client/services/permission/permission.ts

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axios } from "@/client/libs/axios";
import type { PermissionDto, CreatePermissionDto, UpdatePermissionDto } from "@reactive-resume/dto";

// Types locaux pour éviter les dépendances NestJS
export type Permission = PermissionDto;

export const fetchPermissions = async (): Promise<Permission[]> => {
  const response = await axios.get<Permission[]>("/permission");
  return response.data;
};

export const fetchPermission = async (id: string): Promise<Permission> => {
  const response = await axios.get<Permission>(`/permission/${id}`);
  return response.data;
};

export const createPermission = async (data: CreatePermissionDto): Promise<Permission> => {
  const response = await axios.post<Permission>("/permission", data);
  return response.data;
};

export const updatePermission = async (
  id: string,
  data: UpdatePermissionDto,
): Promise<Permission> => {
  const response = await axios.patch<Permission>(`/permission/${id}`, data);
  return response.data;
};

export const deletePermission = async (id: string): Promise<void> => {
  await axios.delete(`/permission/${id}`);
};

// Hooks
export const usePermissions = () => {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: fetchPermissions,
  });
};

export const usePermission = (id: string) => {
  return useQuery({
    queryKey: ["permission", id],
    queryFn: () => fetchPermission(id),
    enabled: !!id,
  });
};

export const useCreatePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
  });
};

export const useUpdatePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePermissionDto }) =>
      updatePermission(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      queryClient.invalidateQueries({ queryKey: ["permission", id] });
    },
  });
};

export const useDeletePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePermission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
  });
};
